import { doc, getDoc, setDoc, updateDoc, deleteField, FieldPath } from 'firebase/firestore';
import { db } from './firebase';
import type { KinderResult } from '../types';
import type { KinderResultDoc } from './kinderResultsCore';

// stripKinderResults + the tree/doc types are firebase-free (kinderResultsCore)
// so tests can import them without booting the SDK; re-export for existing callers.
export { stripKinderResults } from './kinderResultsCore';
export type { KinderResultTree, KinderNoteTree, KinderResultDoc } from './kinderResultsCore';

/**
 * Kindergarten results/notes isolation.
 *
 * Student results (pass/almost/fail per period → student → item) and teacher
 * observation notes are SENSITIVE and were previously embedded on each
 * KinderAssessment, which lives on the classmate-readable class doc. That leaked
 * every child's assessment record to any student in the class, made every
 * keystroke rewrite the whole array (co-teachers clobbering each other,
 * last-write-wins), and pushed a large roster × many periods toward Firestore's
 * 1 MB document ceiling.
 *
 * They now live in per-assessment docs under the owner/co-teacher/admin-only
 * `classes/{classId}/private/kinder_{assessmentId}` subcollection (same access
 * rules as the private student roster — students can NEVER read it). Writes are
 * granular single-cell deep-merges, so two teachers marking different students
 * don't overwrite each other.
 */

const kinderRef = (classId: string, assessmentId: string) =>
  doc(db, 'classes', classId, 'private', `kinder_${assessmentId}`);

/** Load the private results/notes docs for a class's kinder assessments. */
export async function loadKinderResults(
  classId: string, assessmentIds: string[],
): Promise<Record<string, KinderResultDoc>> {
  const out: Record<string, KinderResultDoc> = {};
  await Promise.all(assessmentIds.map(async (id) => {
    try {
      const snap = await getDoc(kinderRef(classId, id));
      if (snap.exists()) out[id] = snap.data() as KinderResultDoc;
    } catch (err) {
      console.error(`[loadKinderResults] failed for assessment ${id} in class ${classId}:`, err);
    }
  }));
  return out;
}

/** Write a full results/notes doc (used at class creation + one-time migration). */
export async function writeKinderResultsDoc(
  classId: string, assessmentId: string, tree: KinderResultDoc,
): Promise<void> {
  await setDoc(kinderRef(classId, assessmentId), tree, { merge: true });
}

/**
 * Set/clear a single result cell. Deep-merges one path so co-teachers marking
 * different students (or the same student on different items) never clobber each
 * other's writes — unlike the old whole-array rewrite.
 */
export async function writeKinderResult(
  classId: string, assessmentId: string, periodId: string, studentId: string, itemId: string,
  value: KinderResult | undefined,
): Promise<void> {
  const ref = kinderRef(classId, assessmentId);
  if (value === undefined) {
    try {
      await updateDoc(ref, new FieldPath('results', periodId, studentId, itemId), deleteField());
    } catch (err) {
      if ((err as { code?: string })?.code !== 'not-found') throw err;
    }
  } else {
    await setDoc(ref, { results: { [periodId]: { [studentId]: { [itemId]: value } } } }, { merge: true });
  }
}

/**
 * Clear many result cells in one period.
 *
 * Deletes each cell by its own field path in a single `updateDoc`, so clearing a
 * 26-item row is ONE write rather than 26 — and, more importantly, it stays a
 * deep-merge delete. Rewriting the student's map wholesale would clobber a
 * co-teacher marking the same child at the same moment, which is the bug the
 * single-cell writer above exists to avoid.
 *
 * Chunked because a "clear every row" on a big class is students x items paths,
 * and one request should not carry thousands of them.
 *
 * CHUNKING IS NOT ATOMIC, and the caller must say so rather than claim nothing
 * happened. If chunk 3 of 4 fails, the first 300 marks really are gone; telling
 * the teacher "they are still saved" would be a lie that costs them a reload to
 * discover. The count of cells successfully cleared rides on the thrown error so
 * the message can be accurate.
 */
export async function clearKinderResults(
  classId: string, assessmentId: string, periodId: string,
  cells: Array<{ studentId: string; itemId: string }>,
): Promise<void> {
  if (!cells.length) return;
  const ref = kinderRef(classId, assessmentId);
  // 400 keeps a single request well inside Firestore's per-write limits while
  // making one request cover any realistic class (30 students x 26 cards = 780).
  const CHUNK = 400;
  let cleared = 0;
  for (let i = 0; i < cells.length; i += CHUNK) {
    const slice = cells.slice(i, i + CHUNK);
    // updateDoc takes (ref, path, value, path, value, …); every value is a delete.
    const args: unknown[] = [];
    for (const c of slice) {
      args.push(new FieldPath('results', periodId, c.studentId, c.itemId), deleteField());
    }
    try {
      await updateDoc(ref, args[0] as FieldPath, args[1], ...args.slice(2));
      cleared += slice.length;
    } catch (err) {
      // No doc means nothing was ever recorded, so there is nothing to clear.
      if ((err as { code?: string })?.code === 'not-found') { cleared += slice.length; continue; }
      throw Object.assign(
        err instanceof Error ? err : new Error(String(err)),
        { clearedCells: cleared, totalCells: cells.length },
      );
    }
  }
}

/** Set/clear a single observation note (empty text clears it). */
export async function writeKinderNote(
  classId: string, assessmentId: string, periodId: string, studentId: string, itemId: string,
  text: string,
): Promise<void> {
  const ref = kinderRef(classId, assessmentId);
  const t = text.trim();
  if (!t) {
    try {
      await updateDoc(ref, new FieldPath('notes', periodId, studentId, itemId), deleteField());
    } catch (err) {
      if ((err as { code?: string })?.code !== 'not-found') throw err;
    }
  } else {
    await setDoc(ref, { notes: { [periodId]: { [studentId]: { [itemId]: t } } } }, { merge: true });
  }
}
